// The care-team primary rules, stated once.
//
// Who may hold a member's primary slot, which primary a later decision may
// replace, and what provenance each kind of write records. Booking, the 1.0
// import, staff writes and the default pick each used to restate these; a rule
// restated in four places drifts in four directions.

import type { ClinicianRole } from '../programs/schema';
import { PRIMARY_INELIGIBLE_ROLES, type CareTeamAssignmentSource } from './care-team';

/** Whether a clinical role may ever hold the primary slot (never a prescriber or physician). */
export function roleMayHoldPrimary(role: ClinicianRole): boolean {
  return !PRIMARY_INELIGIBLE_ROLES.includes(role);
}

/**
 * Whether a seat in this role may hold the primary slot now: the role may hold
 * it at all, and the person still holds a live grant for it. A booking or pool
 * membership proves a grant for some role, not for the role a seat was
 * written with.
 */
export function mayHoldPrimary(role: ClinicianRole, granted: ReadonlySet<ClinicianRole>): boolean {
  return roleMayHoldPrimary(role) && granted.has(role);
}

/**
 * The source of a primary the system picked (default pool, chat entry,
 * outreach). It is the only kind a welcome booking or an imported 1.0 primary
 * may replace; a primary a person chose (staff, a booking, the import) is kept.
 */
export const REPLACEABLE_PRIMARY_SOURCE = 'auto' satisfies CareTeamAssignmentSource;

/** Whether this primary was a system pick that a person's later choice may replace. */
export function isReplaceablePrimary(primary: { source: CareTeamAssignmentSource }): boolean {
  return primary.source === REPLACEABLE_PRIMARY_SOURCE;
}

/** The kinds of decision that write a care-team assignment. */
export type AssignmentDecision = 'system_pick' | 'booking' | 'staff' | 'import';

export interface AssignmentProvenance {
  source: CareTeamAssignmentSource;
  /**
   * When the pairing was confirmed, or null while provisional. A write onto a
   * row that is already confirmed keeps the earlier instant (coalesce).
   */
  confirmedAt: Date | null;
}

/**
 * The provenance every writer records for a decision. A system pick is
 * provisional (`auto`, unconfirmed) until the member engages; a booking, a
 * staff choice and a 1.0 import are each a decided pairing, confirmed now.
 */
export function provenanceFor(decision: AssignmentDecision, now: Date): AssignmentProvenance {
  switch (decision) {
    case 'system_pick':
      return { source: 'auto', confirmedAt: null };
    case 'booking':
      return { source: 'booking', confirmedAt: now };
    case 'staff':
    case 'import':
      return { source: 'manual', confirmedAt: now };
  }
}
